// 23/10/2026 : planning en grille dans l'onglet Planification (Éditer une demande).
// Remplace l'affichage de SOPlanning en iframe (bloqué par X-Frame-Options depuis le 17/10/2026).
// LECTURE SEULE : GET /tasks de l'API SOPlanning (même clé que /users et /projects).
// Un clic dans une case intervenant × jour appelle props.onPick(userId, "AAAA-MM-JJ") ; rien n'est écrit ici.
import React from "react";
import ReactDOM from "react-dom";
import moment from "moment";
import { getSoPlanningTasks } from "../../../store/external/soplanning";
import "./PlanningGrid.css";
// 03/11/2026 (registre n° 38) : vue d'ensemble de la charge
import { readMode, saveMode, ovCols, ovCell, ovCellClass, ovTotals, ovLegend, userColor, textOn, projColor } from "./planningOverview";

const PAL = ["#1f6fd1", "#d62728", "#2a9d3a", "#f28e1c", "#8e44ad", "#8c564b", "#e0569b", "#13a8b8", "#9a9a12", "#2c3e91", "#0f7b6c", "#b8238f", "#c8961a", "#5d6d7e"];
const J3 = ["lun.", "mar.", "mer.", "jeu.", "ven.", "sam.", "dim."];
const MOIS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const SPAN_KEY = "atis_pg_span";

const iso = (m) => m.format("YYYY-MM-DD");
const fr = (s) => (s ? s.slice(8, 10) + "/" + s.slice(5, 7) + "/" + s.slice(0, 4) : "");
const hm = (h) => (h ? String(h).slice(0, 5) : "");
const fixEnc = (t) => {
  t = String(t == null ? "" : t);
  if (/Ã.|Â/.test(t)) { try { return decodeURIComponent(escape(t)); } catch (e) { /* tel quel */ } }
  return t;
};
const monday = (off) => moment().startOf("day").subtract((moment().day() + 6) % 7, "days").add(7 * off, "days");
const tint = (hex, a) => {
  const h = hex.replace("#", "");
  return "rgba(" + parseInt(h.slice(0, 2), 16) + "," + parseInt(h.slice(2, 4), 16) + "," + parseInt(h.slice(4, 6), 16) + "," + a + ")";
};
const readSpan = () => {
  try { const s = parseInt(window.localStorage.getItem(SPAN_KEY), 10); if (s >= 1 && s <= 3) return s; } catch (e) { /* stockage indisponible */ }
  return 2;
};

export default class PlanningGrid extends React.Component {
  constructor(props) {
    super(props);
    this.state = { off: 0, span: readSpan(), tasks: [], loading: true, error: null, full: false, detail: null, mode: readMode() };
    this.wrapRef = React.createRef();
    this.loadedRange = null;
  }

  componentDidMount() {
    this.goToDate(this.props.selStart, true);
    this.onKey = (e) => { if (e.key === "Escape") this.setState({ full: false, detail: null }); };
    document.addEventListener("keydown", this.onKey);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.onKey);
    this.unmounted = true;
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.off !== this.state.off || prevState.span !== this.state.span) this.load();
    if (prevState.full !== this.state.full) this.scrollTo(this.props.selStart || iso(moment()));
    if (prevProps.selStart !== this.props.selStart && this.props.selStart && !this.inView(this.props.selStart)) this.goToDate(this.props.selStart);
    if (this.scrollPending && !this.state.loading) { this.scrollPending = false; this.scrollTo(this.scrollTarget); }
  }

  days() {
    const a = [], m = monday(this.state.off);
    for (let i = 0; i < 7 * this.state.span; i++) a.push(m.clone().add(i, "days"));
    return a;
  }

  inView(ds) {
    const d = this.days();
    return ds >= iso(d[0]) && ds <= iso(d[d.length - 1]);
  }

  goToDate(ds, first) {
    let off = this.state.off;
    if (ds) {
      const w = Math.floor(moment(ds, "YYYY-MM-DD").diff(monday(0), "days") / 7);
      if (w < off || w >= off + this.state.span) off = w;
    }
    this.scrollPending = true;
    this.scrollTarget = ds || iso(moment());
    if (off !== this.state.off) this.setState({ off });
    else if (first) this.load();
    else this.scrollTo(this.scrollTarget);
  }

  load() {
    const d = this.days();
    const from = iso(d[0].clone().subtract(14, "days")), to = iso(d[d.length - 1]);
    const key = from + "|" + to;
    if (this.loadedRange === key && this.state.tasks.length) { this.setState({ loading: false }); return; }
    this.setState({ loading: true, error: null });
    getSoPlanningTasks(from, to).then((r) => {
      if (this.unmounted) return;
      const tasks = (r && r.data && (r.data.data || r.data)) || [];
      this.loadedRange = key;
      this.setState({ tasks: Array.isArray(tasks) ? tasks : [], loading: false });
    }).catch((e) => {
      if (this.unmounted) return;
      this.setState({ loading: false, error: "Planning indisponible (" + (e && e.message ? e.message : "erreur") + ")" });
    });
  }

  scrollTo(ds) {
    const wrap = this.wrapRef.current;
    if (!wrap) return;
    const el = wrap.querySelector('.pg-dh[data-d="' + ds + '"]') || wrap.querySelector(".pg-dh.today");
    // v2 : position mesuree dans le conteneur qui defile, moins la colonne collante des intervenants
    if (!el) { wrap.scrollLeft = 0; return; }
    const k = wrap.querySelector(".pg-k");
    const x = el.getBoundingClientRect().left - wrap.getBoundingClientRect().left + wrap.scrollLeft;
    wrap.scrollLeft = Math.max(0, x - (k ? k.getBoundingClientRect().width : 0));
  }

  // identifiant SOPlanning de la tâche ramené à celui de la liste des intervenants (casse)
  uid(id) {
    id = String(id == null ? "" : id);
    const users = this.props.users || [];
    if (users.some((u) => String(u.value) === id)) return id;
    const f = users.find((u) => String(u.value).toLowerCase() === id.toLowerCase());
    return f ? String(f.value) : id;
  }

  color(id) {
    if (id === "ADM") return "#7f8c8d";
    const ids = (this.props.users || []).map((u) => String(u.value)).filter((x) => x !== "ADM").sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    let i = ids.indexOf(id);
    if (i < 0) { i = 0; for (let j = 0; j < id.length; j++) i = (i * 31 + id.charCodeAt(j)) >>> 0; }
    return PAL[i % PAL.length];
  }

  pick(userId, ds, e) {
    if (this.props.readOnly) return;
    this.setState({ detail: null });
    // 24/10/2026 : Maj (ou Ctrl / Cmd) + clic = ajouter / retirer comme autre intervenant
    if (e && (e.shiftKey || e.ctrlKey || e.metaKey) && this.props.onToggleExtra) { this.props.onToggleExtra(userId); return; }
    this.props.onPick(userId, ds);
  }

  setSpan(span) {
    try { window.localStorage.setItem(SPAN_KEY, String(span)); } catch (e) { /* stockage indisponible */ }
    this.setState({ span });
  }

  renderDetail() {
    const { detail } = this.state;
    if (!detail) return null;
    const t = detail.t, id = this.uid(t.user_id);
    const users = this.props.users || [], projects = this.props.projects || [];
    const uName = (users.find((u) => String(u.value) === id) || {}).label || id;
    const pName = (projects.find((p) => String(p.value) === String(t.project_id)) || {}).label || t.project_id;
    const multi = t.end_date && t.end_date !== t.start_date;
    return (
      <div className="pg-pop" style={{ left: detail.x, top: detail.y }}>
        <span className="pg-who" style={{ background: this.color(id) }}>{uName}</span>
        <div style={{ marginTop: 6 }}>
          <b>{multi ? "Du " + fr(t.start_date) + " au " + fr(t.end_date) : fr(t.start_date)}</b>
          {hm(t.start_hour) ? " · " + hm(t.start_hour) + (hm(t.end_hour) ? "–" + hm(t.end_hour) : "") : ""}
        </div>
        <div className="pg-lab">Projet</div><div>{fixEnc(pName)}</div>
        <div className="pg-lab">Tâche</div><div style={{ whiteSpace: "pre-wrap" }}>{fixEnc(t.title)}</div>
        {t.comment ? (<div><div className="pg-lab">Commentaire</div><div style={{ whiteSpace: "pre-wrap" }}>{fixEnc(t.comment)}</div></div>) : null}
        <div className="pg-acts">
          {!this.props.readOnly ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => this.pick(id, detail.ds)}>Placer la demande ce jour</button>
          ) : null}
          <button type="button" className="btn btn-default btn-sm" onClick={() => this.setState({ detail: null })}>Fermer</button>
        </div>
      </div>
    );
  }

  render() {
    const { tasks, loading, error, full, span } = this.state;
    const ov = this.state.mode !== "detail"; // 03/11/2026 : vue d'ensemble par défaut
    const { selUserId, selStart, selEnd, selTitle, requestId, readOnly } = this.props;
    const extraSet = {};
    (this.props.selExtraIds || []).forEach((x) => { extraSet[String(x).toLowerCase()] = true; });
    const users = (this.props.users || []).filter((u) => u && String(u.label || "").trim());
    const projects = this.props.projects || [];
    const pName = (pid) => fixEnc((projects.find((p) => String(p.value) === String(pid)) || {}).label || pid);
    const pColor = (pid) => projColor(projects, pid);
    const ds = this.days(), today = iso(moment()), d0 = iso(ds[0]), dN = iso(ds[ds.length - 1]);
    const sEnd = selEnd && selEnd >= (selStart || "") ? selEnd : selStart;
    const inSel = (s) => selStart && s >= selStart && s <= sEnd;
    const cols = ov ? ovCols(ds) : "100px " + ds.map((d) => ((d.day() + 6) % 7 > 4 ? "62px" : "138px")).join(" ");
    const title = "Planning — du " + ds[0].date() + " " + MOIS[ds[0].month()] + " au " + ds[ds.length - 1].date() + " " + MOIS[ds[ds.length - 1].month()] + " " + ds[ds.length - 1].year();
    const rows = users.slice().sort((a, b) => {
      const x = String(a.value), y = String(b.value);
      if (x === "ADM") return 1; if (y === "ADM") return -1;
      return String(a.label).localeCompare(String(b.label), "fr");
    });

    const cells = [<div key="k" className="pg-k">Intervenant</div>];
    ds.forEach((d, i) => {
      const s = iso(d), w = (d.day() + 6) % 7;
      cells.push(
        <div key={"h" + s} data-d={s} className={"pg-dh" + (w > 4 ? " we" : "") + (s === today ? " today" : "") + (w === 0 && i > 0 ? " mon" : "") + (inSel(s) ? " sel" : "")}>
          {J3[w]} {d.date()}<small>{s === today ? "aujourd'hui" : MOIS[d.month()]}</small>
        </div>
      );
    });
    rows.forEach((u) => {
      const id = String(u.value), col = this.color(id), bg = tint(col, 0.16);
      const mine = tasks.filter((t) => this.uid(t.user_id) === id && t.start_date);
      const cnt = mine.filter((t) => t.start_date <= dN && (t.end_date || t.start_date) >= d0).length;
      cells.push(
        <div key={"n" + id} className={"pg-n" + (id === String(selUserId || "") || extraSet[id.toLowerCase()] ? " sel" : "")} style={ov ? { background: userColor(u, col), color: textOn(userColor(u, col)) } : { background: col }}>
          {u.label}<small>{cnt} tâche{cnt > 1 ? "s" : ""}</small>
        </div>
      );
      ds.forEach((d, i) => {
        const s = iso(d), w = (d.day() + 6) % 7;
        const list = mine.filter((t) => t.start_date <= s && (t.end_date || t.start_date) >= s)
          .sort((a, b) => (hm(a.start_hour) || "99").localeCompare(hm(b.start_hour) || "99"));
        const isMain = id === String(selUserId || ""), isExtra = !isMain && !!extraSet[id.toLowerCase()];
        const ghost = (isMain || isExtra) && inSel(s);
        cells.push(
          <div key={"c" + id + s} className={"pg-c" + (w > 4 ? " we" : "") + (s === today ? " today" : "") + (w === 0 && i > 0 ? " mon" : "") + (readOnly ? " ro" : "") + (ov ? ovCellClass(list) : "")}
            onClick={(e) => { if (e.target === e.currentTarget) this.pick(id, s, e); }}>
            {ov ? ovCell(list, { pName, pColor, titre: (t) => fixEnc(t.title), onPick: (e) => this.pick(id, s, e) }) : list.map((t) => {
              const multi = t.end_date && t.end_date !== t.start_date;
              const when = hm(t.start_hour) ? hm(t.start_hour) + (hm(t.end_hour) ? "–" + hm(t.end_hour) : "") : (multi ? "↔" : "");
              return (
                <div key={t.task_id + s} className="pg-t" style={{ borderLeftColor: col, background: bg }} title={fixEnc(t.title)}
                  onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); this.setState({ detail: { t, ds: s, x: Math.min(window.innerWidth - 316, Math.max(8, r.left)), y: (r.bottom + 200 > window.innerHeight ? Math.max(8, r.top - 206) : r.bottom + 6) } }); }}>
                  <div className="pg-h">{(when ? when + " · " : "") + pName(t.project_id)}</div>
                  <div className="pg-ti">{fixEnc(t.title)}</div>
                </div>
              );
            })}
            {ghost ? (
              <div className="pg-t pg-ghost">
                <div className="pg-h">{isExtra ? "☆ CETTE DEMANDE" : "★ CETTE DEMANDE"}</div>
                <div className="pg-ti">{(requestId ? requestId + " — " : "") + (selTitle || "")}</div>
              </div>
            ) : null}
            {!readOnly ? (<span className="pg-add" title={"Placer la demande : " + u.label + " le " + fr(s)} onClick={(e) => this.pick(id, s, e)}>＋</span>) : null}
          </div>
        );
      });
    });
    if (ov) ovTotals(rows.map((u) => String(u.value)), tasks, ds, (x) => this.uid(x), iso).forEach((c) => cells.push(c));

    const panel = (
        <div className={"pg-panel" + (full ? " full" : "") + (ov ? " pg-ov" : "")}>
          <div className="pg-bar">
            <span className="pg-title">{title}</span>
            <button type="button" className="pg-nb" title="Semaine précédente" onClick={() => this.setState({ off: this.state.off - 1, detail: null })}>◀</button>
            <button type="button" className="pg-nb" onClick={() => { this.scrollPending = true; this.scrollTarget = today; this.setState({ off: 0, detail: null }); }}>Aujourd'hui</button>
            <button type="button" className="pg-nb" title="Semaine suivante" onClick={() => this.setState({ off: this.state.off + 1, detail: null })}>▶</button>
            <div className="pg-seg">
              <button type="button" aria-pressed={ov ? "true" : "false"} onClick={() => { saveMode("overview"); this.setState({ mode: "overview", detail: null }); }}>Vue d'ensemble</button>
              <button type="button" aria-pressed={ov ? "false" : "true"} onClick={() => { saveMode("detail"); this.setState({ mode: "detail", detail: null }); }}>Détail</button>
            </div>
            <div className="pg-seg">
              {[1, 2, 3].map((n) => (
                <button key={n} type="button" aria-pressed={span === n ? "true" : "false"} onClick={() => this.setSpan(n)}>{n} sem.</button>
              ))}
            </div>
            <button type="button" className="pg-nb" title="Relire le planning" onClick={() => { this.loadedRange = null; this.load(); }}>↻</button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => this.setState({ full: !full, detail: null })}>{full ? "✕ Réduire" : "⛶ Agrandir"}</button>
            {ov ? ovLegend(readOnly) : null}
            {!readOnly ? (
              <div className="pg-hint">Cliquer sur <b>＋</b> dans une case intervenant × jour remplit Intervenant et Début ; <b>Maj + clic</b> sur ＋ ajoute ou retire un autre intervenant. La demande apparaît en vert pointillé. Cliquer une tâche pour la voir.</div>
            ) : null}
          </div>
          {error ? <div className="pg-err">{error}</div> : null}
          <div className="pg-wrap" ref={this.wrapRef} onScroll={() => { if (this.state.detail) this.setState({ detail: null }); }}>
            {loading ? <div className="pg-load">Chargement du planning…</div> : (
              <div className="pg" style={{ gridTemplateColumns: cols }}>{cells}</div>
            )}
          </div>
        </div>
    );
    // plein écran et fiche de tâche rendus dans <body> : la fenêtre « Éditer » (modal bootstrap, transform) ne les enferme pas
    const detail = this.renderDetail();
    return (
      <div>
        {full ? ReactDOM.createPortal(
          <div><div className="pg-backdrop" onClick={() => this.setState({ full: false })} />{panel}</div>, document.body) : panel}
        {detail ? ReactDOM.createPortal(detail, document.body) : null}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/containers/Request/Tab/PlanningGrid.js